import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import {
  LogOut, FileText, CheckCircle, XCircle, Clock,
  DollarSign, Briefcase, AlertCircle, PenTool, X,
  CreditCard, Loader2
} from "lucide-react";

// Signature Pad Component
const SignaturePad = ({ onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const saveSignature = () => {
    if (!hasDrawn) { toast.error("Please sign before saving"); return; }
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="signature-modal">
      <div className="bg-[#15161C] border border-[#2A2B32] rounded-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center space-x-2">
            <PenTool size={20} className="text-[#FF6B35]" />
            <span>Sign Quote</span>
          </h3>
          <button onClick={onCancel} className="p-2 text-[#A0AAB2] hover:text-white"><X size={20} /></button>
        </div>
        <p className="text-[#A0AAB2] text-sm mb-4">By signing, you agree to the terms and pricing of this quote.</p>
        <div className="bg-[#1F2027] border border-[#2A2B32] rounded-xl overflow-hidden mb-4">
          <canvas
            ref={canvasRef}
            className="w-full cursor-crosshair touch-none"
            style={{ height: "180px" }}
            onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
            onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
            data-testid="signature-canvas"
          />
        </div>
        <div className="flex space-x-3">
          <button onClick={clearCanvas} className="flex-1 py-3 bg-[#2A2B32] text-white rounded-lg text-sm font-semibold">Clear</button>
          <button onClick={saveSignature} className="flex-1 py-3 gradient-btn rounded-lg text-sm font-bold" data-testid="save-signature-btn">Sign & Confirm</button>
        </div>
      </div>
    </div>
  );
};

const ClientPortalPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [activeTab, setActiveTab] = useState("quotes");
  const [signingQuoteId, setSigningQuoteId] = useState(null);
  const [payingInvoiceId, setPayingInvoiceId] = useState(null);

  useEffect(() => {
    fetchData();
    // Check for payment return
    const sessionId = searchParams.get("session_id");
    const paymentStatus = searchParams.get("payment");
    if (sessionId && paymentStatus === "success") {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === "cancelled") {
      toast.error("Payment was cancelled");
    }
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, quotesRes] = await Promise.all([
        axios.get(`${API}/client-portal/dashboard`, { withCredentials: true }),
        axios.get(`${API}/quotes`, { withCredentials: true })
      ]);
      setDashboardData(dashRes.data);
      setQuotes(quotesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info("Payment is being processed. It may take a moment to update.");
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      if (res.data.payment_status === "paid") {
        toast.success("Payment successful! Invoice marked as paid.");
        fetchData();
        return;
      } else if (res.data.status === "expired") {
        toast.error("Payment session expired");
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      console.error("Status poll error:", err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleApproveQuote = async (quoteId) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/approve`, {}, { withCredentials: true });
      toast.success("Quote approved! Please sign to confirm.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve quote");
    }
  };

  const handleRejectQuote = async (quoteId) => {
    try {
      await axios.post(`${API}/quotes/${quoteId}/reject`, {}, { withCredentials: true });
      toast.success("Quote rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject quote");
    }
  };

  const handleSignQuote = async (signatureData) => {
    try {
      await axios.post(`${API}/quotes/${signingQuoteId}/sign`, { signature_data: signatureData }, { withCredentials: true });
      toast.success("Quote signed successfully!");
      setSigningQuoteId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign quote");
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    setPayingInvoiceId(invoiceId);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/payments/create-checkout`, {
        invoice_id: invoiceId,
        origin_url: origin
      }, { withCredentials: true });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Payment system unavailable";
      toast.error(msg);
      setPayingInvoiceId(null);
    }
  };

  if (loading) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF6B35]"></div>
      </div>
    );
  }

  const { invoices = [], stats = {} } = dashboardData || {};
  const pendingQuotes = quotes.filter(q => q.status === "sent");
  const approvedQuotes = quotes.filter(q => q.status === "approved");

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10]" data-testid="client-portal-page">
      {/* Header */}
      <div className="bg-[#15161C] border-b border-[#2A2B32]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Client Portal</h1>
              <p className="text-[#A0AAB2] text-sm">Welcome, {user?.name || "Client"}</p>
            </div>
            <button onClick={handleLogout} className="flex items-center space-x-2 px-4 py-2 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-[#A0AAB2] hover:text-white hover:border-[#FF6B35] transition-all" data-testid="client-logout-btn">
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-1">
              <FileText size={18} className="text-blue-400" />
              <span className="text-[#A0AAB2] text-xs">Active Quotes</span>
            </div>
            <p className="text-2xl font-bold text-white">{pendingQuotes.length + approvedQuotes.length}</p>
          </div>
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-1">
              <AlertCircle size={18} className="text-orange-400" />
              <span className="text-[#A0AAB2] text-xs">Needs Action</span>
            </div>
            <p className="text-2xl font-bold text-orange-400">{pendingQuotes.length + approvedQuotes.length}</p>
          </div>
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-1">
              <DollarSign size={18} className="text-green-400" />
              <span className="text-[#A0AAB2] text-xs">Total Invoiced</span>
            </div>
            <p className="text-2xl font-bold text-white">${stats.total_invoiced?.toLocaleString() || 0}</p>
          </div>
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
            <div className="flex items-center space-x-2 mb-1">
              <Clock size={18} className="text-yellow-400" />
              <span className="text-[#A0AAB2] text-xs">Unpaid Invoices</span>
            </div>
            <p className="text-2xl font-bold text-yellow-400">{invoices.filter(i => i.status !== "paid").length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-6">
          <button onClick={() => setActiveTab("quotes")} className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === "quotes" ? "bg-[#FF6B35] text-white" : "bg-[#15161C] border border-[#2A2B32] text-[#A0AAB2] hover:text-white"}`} data-testid="tab-quotes">
            <Briefcase size={18} /><span>Quotes</span>
            {(pendingQuotes.length + approvedQuotes.length) > 0 && <span className={`px-2 py-0.5 text-xs rounded-full ${activeTab === "quotes" ? "bg-white/20" : "bg-[#2A2B32]"}`}>{pendingQuotes.length + approvedQuotes.length}</span>}
          </button>
          <button onClick={() => setActiveTab("invoices")} className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === "invoices" ? "bg-[#FF6B35] text-white" : "bg-[#15161C] border border-[#2A2B32] text-[#A0AAB2] hover:text-white"}`} data-testid="tab-invoices">
            <FileText size={18} /><span>Invoices</span>
          </button>
        </div>

        {/* Quotes Tab */}
        {activeTab === "quotes" && (
          <div className="space-y-4">
            {quotes.length === 0 ? (
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-8 text-center">
                <p className="text-[#A0AAB2]">No quotes yet</p>
              </div>
            ) : (
              quotes.map((quote) => (
                <div key={quote.id} className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6" data-testid={`quote-card-${quote.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-white font-bold text-lg">{quote.title}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          quote.status === "sent" ? "bg-blue-500/20 text-blue-400" :
                          quote.status === "approved" ? "bg-green-500/20 text-green-400" :
                          quote.status === "signed" ? "bg-emerald-500/20 text-emerald-400" :
                          quote.status === "invoiced" ? "bg-purple-500/20 text-purple-400" :
                          quote.status === "rejected" ? "bg-red-500/20 text-red-400" :
                          "bg-gray-500/20 text-gray-400"
                        }`}>{quote.status.toUpperCase()}</span>
                      </div>
                      <p className="text-[#A0AAB2] text-sm">{quote.quote_number}</p>
                      {quote.description && <p className="text-[#A0AAB2] text-sm mt-1">{quote.description}</p>}
                    </div>
                    <p className="text-[#FF6B35] text-2xl font-bold">${quote.total_price?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                  </div>
                  {/* Line Items */}
                  <div className="bg-[#1F2027] rounded-lg p-4 mb-4">
                    <p className="text-xs text-[#A0AAB2] font-medium mb-2 uppercase">Items</p>
                    {(quote.line_items || []).map((item, idx) => (
                      <div key={item.id || idx} className="flex items-center justify-between py-2 border-b border-[#2A2B32] last:border-0">
                        <span className="text-white text-sm">{item.description}</span>
                        <span className="text-white text-sm font-medium">${item.price?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-3 mt-2 border-t border-[#FF6B35]/30">
                      <span className="text-white font-bold">Total</span>
                      <span className="text-[#FF6B35] font-bold text-lg">${quote.total_price?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                    </div>
                  </div>
                  {/* Actions */}
                  {quote.status === "sent" && (
                    <div className="flex space-x-3">
                      <button onClick={() => handleRejectQuote(quote.id)} className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors font-semibold" data-testid={`reject-quote-${quote.id}`}>
                        <XCircle size={18} /><span>Reject</span>
                      </button>
                      <button onClick={() => handleApproveQuote(quote.id)} className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors font-semibold" data-testid={`approve-quote-${quote.id}`}>
                        <CheckCircle size={18} /><span>Approve</span>
                      </button>
                    </div>
                  )}
                  {quote.status === "approved" && (
                    <button onClick={() => setSigningQuoteId(quote.id)} className="w-full flex items-center justify-center space-x-2 px-4 py-3 gradient-btn rounded-lg font-bold" data-testid={`sign-quote-${quote.id}`}>
                      <PenTool size={18} /><span>Sign Quote</span>
                    </button>
                  )}
                  {quote.status === "signed" && (
                    <div className="flex items-center justify-center space-x-2 text-emerald-400 py-3">
                      <CheckCircle size={20} /><span className="font-semibold">Signed - Invoice incoming</span>
                    </div>
                  )}
                  {quote.status === "invoiced" && (
                    <div className="flex items-center justify-center space-x-2 text-purple-400 py-3">
                      <FileText size={20} /><span className="font-semibold">Invoiced</span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div className="space-y-4">
            {invoices.length === 0 ? (
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-8 text-center">
                <p className="text-[#A0AAB2]">No invoices yet</p>
              </div>
            ) : (
              invoices.map((invoice) => (
                <div key={invoice.id} className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6" data-testid={`invoice-card-${invoice.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-white font-bold">{invoice.invoice_number}</h3>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          invoice.status === "paid" ? "bg-green-500/20 text-green-400" :
                          invoice.status === "overdue" ? "bg-red-500/20 text-red-400" :
                          "bg-yellow-500/20 text-yellow-400"
                        }`} data-testid={`invoice-status-${invoice.id}`}>
                          {invoice.status === "paid" ? "PAID" : "UNPAID"}
                        </span>
                      </div>
                      <p className="text-[#A0AAB2] text-sm whitespace-pre-line">{invoice.job_description}</p>
                      {invoice.due_date && <p className="text-[#A0AAB2] text-xs mt-1">Due: {invoice.due_date}</p>}
                    </div>
                    <div className="text-right flex flex-col items-end gap-3">
                      <p className={`text-2xl font-bold ${invoice.status === "paid" ? "text-green-400" : "text-[#FF6B35]"}`}>
                        ${invoice.amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}
                      </p>
                      {invoice.status !== "paid" && (
                        <button
                          onClick={() => handlePayInvoice(invoice.id)}
                          disabled={payingInvoiceId === invoice.id}
                          className="flex items-center space-x-2 px-5 py-2.5 gradient-btn rounded-lg font-bold text-sm disabled:opacity-50"
                          data-testid={`pay-invoice-${invoice.id}`}
                        >
                          {payingInvoiceId === invoice.id ? (
                            <><Loader2 size={16} className="animate-spin" /><span>Processing...</span></>
                          ) : (
                            <><CreditCard size={16} /><span>Pay Now</span></>
                          )}
                        </button>
                      )}
                      {invoice.status === "paid" && (
                        <div className="flex items-center space-x-1 text-green-400 text-sm">
                          <CheckCircle size={16} />
                          <span>Payment Complete</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Contact */}
        <div className="mt-6 bg-gradient-to-r from-[#4A2B6E]/30 via-[#0D5C63]/30 to-[#FF6B35]/30 border border-[#2A2B32] rounded-xl p-6 text-center">
          <h3 className="text-white font-semibold mb-2">Have Questions?</h3>
          <p className="text-[#A0AAB2] mb-4">Contact us anytime</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:863-340-9244" className="inline-flex items-center justify-center space-x-2 gradient-btn px-6 py-3 rounded-lg font-semibold"><span>Call: 863-340-9244</span></a>
            <a href="mailto:newleafpmcllc@gmail.com" className="inline-flex items-center justify-center bg-[#1F2027] border border-[#2A2B32] text-white px-6 py-3 rounded-lg font-semibold hover:border-[#FF6B35] transition-all"><span>Email Us</span></a>
          </div>
        </div>
      </div>

      {signingQuoteId && <SignaturePad onSave={handleSignQuote} onCancel={() => setSigningQuoteId(null)} />}
    </div>
  );
};

export default ClientPortalPage;
